import { API_CONFIG } from '../config';
import { getContentForLangSync, normalizeContentLang } from './contentService';
import { getUserEmail } from './userEmail';
import { getClientId } from './clientId';
import { getSessionToken, notifySessionExpired } from './ltiSession';

// Construye las cabeceras LTI (Authorization + X-Conversation-Id) que el
// backend de LangGraph espera. Lanza NO_SESSION_TOKEN si el alumno entro al
// simulador sin pasar por el handshake LTI (URL directa, sesion vencida).
// Exportado para tests.
// Dev bypass: con REACT_APP_DISABLE_LTI=true no exigimos token (utility cuando
// el backend tambien corre con LTI_SESSION_REQUIRED=false). Doble candado:
// ademas exigimos NODE_ENV !== 'production' para que un `.env.local` filtrado
// en el build de prod no deshabilite la auth.
const DISABLE_LTI =
    process.env.REACT_APP_DISABLE_LTI === 'true'
    && process.env.NODE_ENV !== 'production';

export const buildLTIHeaders = () => {
    const token = getSessionToken();
    if (!token) {
        if (DISABLE_LTI) {
            const headers = {};
            let conversationId = null;
            try {
                conversationId = window.localStorage.getItem('thread_id');
            } catch (_) {}
            if (conversationId) headers['X-Conversation-Id'] = conversationId;
            return headers;
        }
        const err = new Error('NO_SESSION_TOKEN');
        err.code = 'NO_SESSION_TOKEN';
        throw err;
    }
    const headers = { Authorization: `Bearer ${token}` };
    let conversationId = null;
    try {
        conversationId = window.localStorage.getItem('thread_id');
    } catch (_) {}
    if (conversationId) headers['X-Conversation-Id'] = conversationId;
    return headers;
};

// Helper fetch con manejo de errores para endpoints no-stream.
// Exportado para tests.
export const apiFetch = async (endpoint, options = {}) => {
    const url = `${API_CONFIG.BASE_URL}${endpoint}`;

    const defaultOptions = {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...buildLTIHeaders(),
            ...(options.headers || {}),
        },
    };

    const response = await fetch(url, defaultOptions);

    if (response.status === 401) {
        notifySessionExpired();
        const err = new Error('SESSION_EXPIRED');
        err.code = 'SESSION_EXPIRED';
        throw err;
    }

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
    }

    return response;
};

// Construye el payload para /simulation y /simulation/stream.
// Idioma: el backend acepta `locale` o `language` en la raíz o en simulation_config (normaliza a es|en).
// Si es el primer mensaje y hay scenarioConfig, añade simulation_config.
const buildSimulationPayload = (
    threadId,
    message,
    isFirstMessage,
    scenarioConfig,
    selectedSkills,
    selectedLevel,
    contentLang = 'es',
    sessionDurationSeconds = null,
) => {
    const locale = normalizeContentLang(contentLang);

    const userEmail = getUserEmail();
    const clientId = getClientId();

    const payload = {
        user_response: message,
        thread_id: threadId,
        locale,
        client_id: clientId,
        ...(userEmail && { user_email: userEmail }),
        ...(sessionDurationSeconds != null && { session_duration_seconds: sessionDurationSeconds }),
    };

    if (isFirstMessage && scenarioConfig) {
        const { skillSlugs, skillConfigTemplates } = getContentForLangSync(contentLang);
        const firstSkillKey = selectedSkills[0] || null;
        const habilidad = firstSkillKey
            ? (skillSlugs[firstSkillKey] || firstSkillKey.toLowerCase().replace(/\s+/g, '_'))
            : 'comunicacion';

        payload.simulation_config = {
            locale,
            habilidad,
            nivel: selectedLevel || 2,
            ...(userEmail && { user_email: userEmail }),
            client_id: clientId,
            turnos_minimos: 2,
            turnos_maximos: 3,
            escenario: scenarioConfig.nombre,
            tipo_proyecto: scenarioConfig.tipo_proyecto,
            instrucciones_actuacion: scenarioConfig.instrucciones_actuacion,
            descripcion_breve: scenarioConfig.descripcion_breve,
            descripcion_detallada: scenarioConfig.descripcion_detallada,
            character: {
                name: scenarioConfig.nombre_personaje || null,
                role: scenarioConfig.cargo_personaje
                    || (locale === 'en' ? 'project representative' : 'representante del proyecto'),
                goal: locale === 'en'
                    ? 'obtain credit approval while minimizing or avoiding the environmental and social requirements raised by the specialist'
                    : 'obtener la aprobación del crédito minimizando o evitando los requerimientos ambientales y sociales que el especialista le plantea'
            },
            tone_base: locale === 'en' ? 'institutional_diplomatic' : 'institucional_diplomatico',
            skill_config: {
                [habilidad]: skillConfigTemplates[habilidad] || {}
            }
        };
    }

    return payload;
};

// Servicios de la API
export const apiService = {
    // Streaming SSE: POST /simulation/stream.
    // Handlers: { onToken, onObserver, onScene, onMessageDone, onDone, onError }.
    //   onToken({ text, node })
    //   onObserver({ progress, stance, body_language, hint, mastery_signals })
    //   onScene({ scene_moment, scene_moment_id, visit_stage, scene_moments_shown })
    //   onMessageDone({ text, node, phase })
    //   onDone(finalPayload)  // mismo shape que /simulation
    //   onError({ status, message })
    sendMessageStream: async (
        threadId,
        message,
        isFirstMessage = false,
        scenarioConfig = null,
        contentLang = 'es',
        selectedSkills = [],
        selectedLevel = 2,
        handlers = {},
        signal,
        sessionDurationSeconds = null,
    ) => {
        const { onToken, onObserver, onScene, onMessageDone, onDone, onError } = handlers;
        const body = buildSimulationPayload(
            threadId,
            message,
            isFirstMessage,
            scenarioConfig,
            selectedSkills,
            selectedLevel,
            contentLang,
            sessionDurationSeconds,
        );

        console.log('📤 Payload enviado a /simulation/stream:', JSON.stringify(body, null, 2));

        const url = `${API_CONFIG.BASE_URL}/simulation/stream`;
        const resp = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...buildLTIHeaders(),
            },
            body: JSON.stringify(body),
            signal,
        });

        if (resp.status === 401) {
            notifySessionExpired();
            const err = new Error('SESSION_EXPIRED');
            err.code = 'SESSION_EXPIRED';
            throw err;
        }

        if (!resp.ok || !resp.body) {
            throw new Error(`stream HTTP ${resp.status} - ${resp.statusText}`);
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const raw of parts) {
                let event = 'message';
                let data = '';
                for (const line of raw.split('\n')) {
                    if (line.startsWith(':')) continue; // comentario SSE (keepalive)
                    if (line.startsWith('event: ')) event = line.slice(7).trim();
                    else if (line.startsWith('data: ')) data += line.slice(6);
                }
                if (!data) continue;

                let payload;
                try {
                    payload = JSON.parse(data);
                } catch {
                    continue;
                }

                if (event === 'token') onToken?.(payload);
                else if (event === 'observer') onObserver?.(payload);
                else if (event === 'scene') onScene?.(payload);
                else if (event === 'message_done') onMessageDone?.(payload);
                else if (event === 'done') onDone?.(payload);
                else if (event === 'error') onError?.(payload);
            }
        }
    },

    // Obtener clave efímera para WebRTC
    getEphemeralKey: async (language = 'es') => {
        const payload = {
            model: API_CONFIG.DEPLOYMENT,
            voice: API_CONFIG.VOICE,
            input_audio_transcription: {
                model: 'gpt-4o-mini-transcribe',
                language: language
            },
            turn_detection: {
                type: 'server_vad',
                threshold: 0.5,
                prefix_padding_ms: 300,
                silence_duration_ms: 1200,
                create_response: true
            }
        };

        const response = await apiFetch('/realtime/session', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        return response.json();
    },

    // Convertir texto a audio (TTS) — ahora usa /tts/stream.
    // Devuelve un Blob reproducible (el servidor acumula el stream como audio/mpeg).
    textToSpeech: async (text, locale = 'es') => {
        const response = await apiFetch('/tts/stream', {
            method: 'POST',
            body: JSON.stringify({
                text,
                voice: API_CONFIG.VOICE,
                locale,
                speed: 1.0,
            })
        });

        return response.blob();
    },

    // Cargar mensajes previos
    loadPreviousMessages: async (threadId) => {
        const response = await apiFetch(`/checkpoints?thread_id=${threadId}`);
        return response.json();
    },

    // Enviar encuesta de feedback post-simulacion.
    // payload: { thread_id, client_id, ueq, likert, open_text, comment, email, locale, metadata }
    submitFeedbackSurvey: async (payload) => {
        const response = await apiFetch('/feedback/survey', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        return response.json();
    },
};

export default apiService;
